import type { ResolvedText } from '@/services/myBenefits'

/**
 * Where this app keeps the phrases the server names.
 *
 * The server sends a bare key — it cannot know that we file these under `benefits.` while the
 * mobile app files them under `common.` — so the namespace is added here, once.
 */
export const REDEMPTION_NAMESPACE = 'benefits.'

/**
 * The slice of i18next this needs: look a phrase up, and say whether we have it.
 *
 * Narrowed to two methods so the rule is stated in one place and reads without a provider. The
 * `i18n` object from `useTranslation()` satisfies it.
 */
export interface Localizer {
  t: (key: string, args?: Record<string, string>) => string
  exists: (key: string) => boolean
}

/**
 * Render one line of server-authored redemption copy, in the reader's language where we have it.
 *
 * Three cases, in order:
 * 1. **No key** — an admin's own words, which exist in one language only. Show them.
 * 2. **A key we ship** — the shipped default copy. Show OUR translation, interpolated with the
 *    server's values, so this page stays translated into the six languages it already supported.
 * 3. **A key we do not ship** — a newer server, or a phrase added after this deploy. Show the
 *    server's own wording, which is always sent for exactly this reason.
 *
 * Case 3 is why `text` is never optional on the wire. Web can be redeployed quickly and mobile
 * cannot, but the contract is one contract, and a client that went blank on an unrecognized key
 * would be a client that breaks every time somebody adds a line.
 */
export const redemptionText = (localizer: Localizer, line: ResolvedText | null | undefined): string => {
  if (!line) return ''
  if (!line.i18nKey) return line.text

  const key = `${REDEMPTION_NAMESPACE}${line.i18nKey}`
  // `exists` rather than comparing `t(key)` to the key: i18next falls back to English before it
  // gives up, and a phrase that legitimately equals its own key would then look missing.
  if (!localizer.exists(key)) return line.text
  return localizer.t(key, line.args ?? {})
}
